import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { assignSubscription, listPlans } from '@/lib/api/admin';
import { describeError } from '@/lib/api/client';
import type { PaymentMethod, Subscription } from '@/lib/api/types';
import { formatDate } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input, Select } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import { addDays, addMonths, defaultAmount, extensionBaseFor, parseAmount, todayInTashkent } from './assign';

const STEPS = [1, 3, 12] as const;
const GRACE_DAYS = 7;

export const METHOD_LABEL: Record<PaymentMethod, string> = {
  cash: 'Наличные',
  transfer: 'Перечисление',
  other: 'Другое',
};

/** Invalidate everything that shows an organization's subscription or its invoices. */
export function invalidateSubscription(qc: ReturnType<typeof useQueryClient>, organizationId: string) {
  void qc.invalidateQueries({ queryKey: ['subscription', organizationId] });
  void qc.invalidateQueries({ queryKey: ['organization', organizationId] });
  void qc.invalidateQueries({ queryKey: ['admin', 'subscriptions'] });
}

/**
 * Put an organization on a plan until a day, by hand — and, when money was
 * taken outside the gateway, record it as a paid invoice. +N months count
 * from the end of a period still running, so an early extension loses no days.
 * Render it only while open: its form state is the dialog's lifetime.
 */
export function AssignDialog({ organizationId, subscription, onClose }: {
  organizationId: string;
  /** Null when the organization has no subscription row yet. */
  subscription: Subscription | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const plans = useQuery({ queryKey: ['admin', 'plans'], queryFn: listPlans });
  const today = todayInTashkent();
  const base = extensionBaseFor(today, subscription);

  // The dialog is mounted only while open, so every open starts fresh:
  // the organization's current plan, +1 month.
  const [planId, setPlanId] = useState(subscription?.plan.id ?? '');
  const [months, setMonths] = useState<number | null>(1);
  const [until, setUntil] = useState(() => addMonths(base, 1));
  const [record, setRecord] = useState(false);
  // null = follow the plan and the months chosen; a string = what the admin typed.
  const [amountInput, setAmountInput] = useState<string | null>(null);
  const [currencyInput, setCurrencyInput] = useState<string | null>(null);
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [note, setNote] = useState('');

  const sortedPlans = useMemo(
    () => [...(plans.data ?? [])].sort((a, b) => a.priceMonthly - b.priceMonthly),
    [plans.data],
  );
  const plan = sortedPlans.find((p) => p.id === planId) ?? null;
  const amount = amountInput ?? (plan ? String(defaultAmount(plan, months ?? 1)) : '');
  const currency = currencyInput ?? plan?.currency ?? '';

  const parsedAmount = parseAmount(amount);
  const pastDate = until < today;
  const canSave = !!plan && !!until && !pastDate && (!record || (parsedAmount !== null && /^[A-Z]{3}$/.test(currency)));

  const save = useMutation({
    mutationFn: () => assignSubscription(organizationId, {
      planId,
      until,
      ...(record && parsedAmount !== null
        ? { payment: { amount: parsedAmount, currency, method, ...(note.trim() ? { note: note.trim() } : {}) } }
        : {}),
    }),
    onSuccess: () => {
      toast('success', 'Подписка назначена', `${plan?.name ?? ''} — по ${formatDate(until)}`);
      invalidateSubscription(qc, organizationId);
      onClose();
    },
    onError: (err) => toast('error', 'Не удалось назначить подписку', describeError(err)),
  });

  const pickMonths = (n: number) => {
    setMonths(n);
    setUntil(addMonths(base, n));
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title="Назначить или продлить подписку"
      description="Тариф действует по выбранный день включительно, затем 7 дней льготы, после — Free. Онлайн-шлюз такую подписку не списывает."
      footer={(
        <>
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button loading={save.isPending} disabled={!canSave} onClick={() => save.mutate()}>Сохранить</Button>
        </>
      )}
    >
      <div className="space-y-4 px-5 py-4">
        <Select
          label="Тариф"
          value={planId}
          onChange={(e) => setPlanId(e.target.value)}
          options={[
            ...(planId ? [] : [{ value: '', label: 'Выберите тариф' }]),
            ...sortedPlans.map((p) => ({ value: p.id, label: p.isActive ? p.name : `${p.name} (скрыт)` })),
          ]}
        />

        <div>
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted">Срок</div>
          <div className="flex flex-wrap items-end gap-2">
            {STEPS.map((n) => (
              <Button key={n} size="sm" variant={months === n ? 'primary' : 'outline'} onClick={() => pickMonths(n)}>
                +{n} мес
              </Button>
            ))}
            <Input
              aria-label="Действует по"
              type="date"
              min={today}
              value={until}
              onChange={(e) => { setUntil(e.target.value); setMonths(null); }}
              error={pastDate ? 'Дата уже прошла' : undefined}
            />
          </div>
          <p className="mt-1.5 text-xs text-faint">
            {base > today
              ? `Отсчёт от конца текущего срока (${formatDate(base)}).`
              : 'Отсчёт от сегодняшнего дня.'}
          </p>
        </div>

        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-ink">Записать оплату</div>
            <div className="text-xs text-muted">Деньги получены вне онлайн-шлюза — появится оплаченный счёт.</div>
          </div>
          <Switch checked={record} onChange={setRecord} label="Записать оплату" />
        </div>

        {record && (
          <div className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-[1fr_96px]">
            <Input
              label="Сумма"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmountInput(e.target.value)}
              error={parsedAmount === null ? 'Положительная сумма, до 2 знаков после запятой' : undefined}
            />
            <Input
              label="Валюта"
              value={currency}
              maxLength={3}
              onChange={(e) => setCurrencyInput(e.target.value.toUpperCase())}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Select
              label="Способ"
              value={method}
              onChange={(e) => setMethod(e.target.value as PaymentMethod)}
              options={(Object.keys(METHOD_LABEL) as PaymentMethod[]).map((m) => ({ value: m, label: METHOD_LABEL[m] }))}
            />
            <Input label="Комментарий" value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
          </div>
          </div>
        )}

        {plan && !pastDate && until && (
          <div className="rounded-lg bg-surface-2 px-3 py-2 text-sm text-ink">
            <b>{plan.name}</b> по {formatDate(until)}, льгота по {formatDate(addDays(until, GRACE_DAYS))}
          </div>
        )}
      </div>
    </Dialog>
  );
}
